import * as React from 'react';

const standardKeyboardClickIntentElements = new WeakSet<HTMLElement>();

/**
 * Records explicit keyboard activation intent (Enter/Space) on an element so
 * `accessKeyAwareOnClickWrapper` can allow the corresponding click to proceed.
 *
 * Important: use this in tandem with `accessKeyAwareOnClickWrapper` on the same
 * element (`onKeyDown` + `onClick`), otherwise keyboard clicks may be misclassified.
 */
export function markStandardKeyboardClickIntent({
  key,
  currentTarget,
}: React.KeyboardEvent<HTMLElement>): void {
  if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
    standardKeyboardClickIntentElements.add(currentTarget);
  }
}

/**
 * Use this function to wrap onClick handlers for elements that have an access key, when furthermore
 * you want the access key to only focus the element, but not take any further action.
 *
 * Important: use this together with `markStandardKeyboardClickIntent` on the same element.
 */
export function accessKeyAwareOnClickWrapper({
  event,
  onStandardClick,
}: {
  event: React.MouseEvent<HTMLElement>;
  onStandardClick(event: React.MouseEvent<HTMLElement>): void;
}): void {
  const { currentTarget } = event;
  if (standardKeyboardClickIntentElements.has(currentTarget) || event.detail > 0) {
    standardKeyboardClickIntentElements.delete(currentTarget);
    onStandardClick(event);
  } else {
    // Now we assume that the onClick signal came from the access key.
    event.preventDefault();
    event.stopPropagation();
    currentTarget.focus();
  }
}
